import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import HardBreak from "@tiptap/extension-hard-break";
import Mention from "@tiptap/extension-mention";
import { Placeholder, UndoRedo } from "@tiptap/extensions";
import { MentionPicker } from "../mention-picker";
import { buildMentionOptions } from "../../util/mentions";
import { MAX_MESSAGE_LENGTH } from "../../util/common";
import "./chat-composer.css";

// The receiver hides the typing indicator 1s after the last event, so emit a bit faster than that.
const TYPING_THROTTLE_MS = 800;
// Same characters the old textarea composer accepted inside a mention query.
const QUERY_PATTERN = /^[\w ]*$/;
// Show the character counter once the message is within 10% of the limit.
const COUNTER_FROM = Math.floor(MAX_MESSAGE_LENGTH * 0.9);

const MentionPickerKey = new PluginKey("chatComposerMention");

const ComposerKeymap = Extension.create({
  name: "composerKeymap",
  // Run before the core Enter handler (splitBlock) so Enter can send.
  priority: 1000,
  addOptions() {
    return { onEnter: () => false };
  },
  addKeyboardShortcuts() {
    return {
      Enter: () => this.options.onEnter(),
    };
  },
});

function serialize(editor) {
  return editor.getText({ blockSeparator: "\n" });
}

function collectMentions(editor) {
  const mentions = [];
  const seen = new Set();
  editor.state.doc.descendants((node) => {
    if (node.type.name !== "mention") return;
    const key = String(node.attrs.id);
    if (seen.has(key)) return;
    seen.add(key);
    mentions.push({ id: node.attrs.id, name: node.attrs.label });
  });
  return mentions;
}

function ChatComposer(props, ref) {
  const {
    onlineUsers,
    placeholder,
    disabled = false,
    variant,
    listId = "mention-picker-list",
  } = props;

  const propsRef = useRef(props);
  propsRef.current = props;

  const onlineUserIds = useMemo(() => {
    const ids = new Set();
    (onlineUsers || []).forEach((user) => {
      if (user?.is_online && user.user_id != null) ids.add(user.user_id);
    });
    return ids;
  }, [onlineUsers]);
  const onlineUserIdsRef = useRef(onlineUserIds);
  onlineUserIdsRef.current = onlineUserIds;

  const [picker, setPicker] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // null below COUNTER_FROM so ordinary typing doesn't re-render the composer.
  const [charCount, setCharCount] = useState(null);
  const pickerRef = useRef(null);
  const activeIndexRef = useRef(0);
  const lastTypingRef = useRef(0);

  const getOptions = (query) =>
    buildMentionOptions({
      members: propsRef.current.members,
      query,
      includeAiCommand: propsRef.current.includeAiCommand,
      onlineUserIds: onlineUserIdsRef.current,
    });

  const updateActiveIndex = (index) => {
    activeIndexRef.current = index;
    setActiveIndex(index);
  };

  const openPicker = ({ query, items, command }) => {
    const previous = pickerRef.current;
    const next = { query, items, command };
    pickerRef.current = next;
    setPicker(next);
    if (!previous || previous.query !== query) {
      updateActiveIndex(0);
    } else if (activeIndexRef.current >= items.length) {
      updateActiveIndex(Math.max(0, items.length - 1));
    }
  };

  const closePicker = () => {
    pickerRef.current = null;
    setPicker(null);
    updateActiveIndex(0);
  };

  const selectOption = (option) => {
    const current = pickerRef.current;
    if (!option || !current) return;
    current.command({ id: option.id, label: option.name });
    propsRef.current.onSelectOption?.(option);
  };

  const handlePickerKey = (event) => {
    const items = pickerRef.current?.items || [];

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (items.length > 0) {
        const step = event.key === "ArrowDown" ? 1 : -1;
        updateActiveIndex(
          (activeIndexRef.current + step + items.length) % items.length,
        );
      }
      return true;
    }

    if ((event.key === "Enter" && !event.shiftKey) || event.key === "Tab") {
      if (items.length === 0) return false;
      selectOption(items[Math.min(activeIndexRef.current, items.length - 1)]);
      return true;
    }

    return event.key === "Escape";
  };

  const editor = useEditor({
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    editable: !disabled,
    extensions: [
      Document,
      Paragraph,
      Text,
      HardBreak,
      UndoRedo,
      Placeholder.configure({
        placeholder: () => propsRef.current.placeholder || "",
      }),
      ComposerKeymap.configure({
        onEnter: () => {
          // Let the open picker take Enter when it has something to pick.
          if (pickerRef.current?.items.length) return false;
          if (!propsRef.current.submitOnEnter) return false;
          propsRef.current.onSubmit?.();
          return true;
        },
      }),
      Mention.configure({
        HTMLAttributes: { class: "mention-chip" },
        deleteTriggerWithBackspace: true,
        renderText: ({ node }) => `@@${node.attrs.label}@@`,
        suggestion: {
          char: "@",
          pluginKey: MentionPickerKey,
          allowSpaces: true,
          allow: ({ state, range }) => {
            const $from = state.doc.resolve(range.from);
            if (!$from.parent.type.contentMatch.matchType(state.schema.nodes.mention)) {
              return false;
            }
            const query = state.doc.textBetween(range.from + 1, range.to);
            if (/^\s/.test(query) || !QUERY_PATTERN.test(query)) return false;
            // Once the query has a space, keep the picker only while it still matches someone.
            return !/\s/.test(query) || getOptions(query).length > 0;
          },
          items: ({ query }) => getOptions(query),
          render: () => ({
            onStart: openPicker,
            onUpdate: openPicker,
            onExit: closePicker,
            onKeyDown: ({ event }) => handlePickerKey(event),
          }),
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: "chat-composer__editor",
        role: "textbox",
        "aria-multiline": "true",
        "aria-autocomplete": "list",
      },
      handlePaste: (view, event) => {
        propsRef.current.onPaste?.(event);
        const items = Array.from(event.clipboardData?.items || []);
        return items.some((item) => item.kind === "file");
      },
    },
    onUpdate: ({ editor: instance }) => {
      const text = serialize(instance);
      propsRef.current.onTextChange?.(text);
      setCharCount(text.length >= COUNTER_FROM ? text.length : null);

      if (instance.isEmpty) return;
      const now = Date.now();
      if (now - lastTypingRef.current >= TYPING_THROTTLE_MS) {
        lastTypingRef.current = now;
        propsRef.current.onTyping?.();
      }
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(!disabled);
    // Placeholder text is a decoration, so nudge it to pick up the new copy.
    editor.view.dispatch(editor.state.tr.setMeta("addToHistory", false));
  }, [editor, disabled, placeholder]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const dom = editor.view.dom;
    dom.setAttribute("aria-expanded", picker ? "true" : "false");
    if (picker) {
      dom.setAttribute("aria-controls", listId);
      dom.setAttribute("aria-activedescendant", `${listId}-option-${activeIndex}`);
    } else {
      dom.removeAttribute("aria-controls");
      dom.removeAttribute("aria-activedescendant");
    }
  }, [editor, picker, activeIndex, listId]);

  useImperativeHandle(
    ref,
    () => ({
      getMessage: () => ({
        text: serialize(editor),
        mentions: collectMentions(editor),
      }),
      isEmpty: () => editor.isEmpty,
      focus: () => editor.commands.focus("end"),
      clear: () => {
        editor.commands.clearContent(true);
        lastTypingRef.current = 0;
      },
      // Replaces the composer with `[mention chip] template` and selects the first [placeholder].
      applyTemplate: ({ id, label }, template) => {
        editor
          .chain()
          .setContent({
            type: "doc",
            content: [
              {
                type: "paragraph",
                content: [
                  { type: "mention", attrs: { id, label } },
                  { type: "text", text: ` ${template}` },
                ],
              },
            ],
          })
          .run();

        // Paragraph opens at 1, the mention atom takes 1, then the leading space.
        const textStart = 3;
        const open = template.indexOf("[");
        const close = template.indexOf("]", open);
        if (open !== -1 && close !== -1) {
          editor
            .chain()
            .focus()
            .setTextSelection({ from: textStart + open, to: textStart + close + 1 })
            .run();
        } else {
          editor.commands.focus("end");
        }
      },
    }),
    [editor],
  );

  return (
    <div className={`chat-composer${variant === "thread" ? " chat-composer--thread" : ""}`}>
      {picker && (
        <MentionPicker
          query={picker.query}
          options={picker.items}
          activeIndex={activeIndex}
          onActiveIndexChange={updateActiveIndex}
          onSelect={selectOption}
          listId={listId}
        />
      )}
      <EditorContent
        editor={editor}
        className={`chat-composer__field${disabled ? " is-disabled" : ""}`}
      />
      {charCount !== null && (
        <div
          className={`chat-composer__counter${
            charCount > MAX_MESSAGE_LENGTH ? " is-over" : ""
          }`}
          aria-live="polite"
        >
          {charCount.toLocaleString()} / {MAX_MESSAGE_LENGTH.toLocaleString()}
          {charCount > MAX_MESSAGE_LENGTH && " (too long to send)"}
        </div>
      )}
    </div>
  );
}

export default memo(forwardRef(ChatComposer));
